import request from '../../utils/request';
import { Result } from '../../models/general';

/**
 * Module configuration view model from API
 */
export interface ModuleConfigView {
  moduleId: string;
  isEnabled: boolean;
  displayOrder?: number;
}

/**
 * Page configuration view model from API
 */
export interface PageConfigView {
  moduleId: string;
  pagePath: string;
  isEnabled: boolean;
}

/**
 * Input model for updating page configuration
 */
export interface PageConfigInput {
  moduleId: string;
  pagePath: string;
  isEnabled: boolean;
}

/**
 * Company module configuration view model from API (Operator only)
 */
export interface CompanyModuleConfigView {
  companyId: number;
  companyName: string;
  modules: ModuleConfigView[];
  pages: PageConfigView[];
}

/**
 * Input model for updating module configuration
 */
export interface ModuleConfigInput {
  moduleId: string;
  isEnabled: boolean;
  displayOrder?: number;
}

/**
 * Get module configuration for current user's company.
 * Returns empty array on error (show all modules by default).
 */
export async function getModuleConfig(): Promise<ModuleConfigView[]> {
  try {
    const response = await request.get<Result<ModuleConfigView[]>>('navigation/modules');
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch module config:', error);
    return []; // Return empty on error (show all modules by default)
  }
}

/**
 * Get module configuration for a specific company (Operator only).
 */
export async function getCompanyModuleConfig(companyId: number): Promise<CompanyModuleConfigView | null> {
  try {
    const response = await request.get<Result<CompanyModuleConfigView>>(
      `navigation/modules/company/${companyId}`
    );
    return response.data?.payload ?? null;
  } catch (error) {
    console.error('Failed to fetch company module config:', error);
    return null;
  }
}

/**
 * Update module configuration for a company (Operator only).
 */
export async function updateCompanyModuleConfig(
  companyId: number,
  modules: ModuleConfigInput[]
): Promise<boolean> {
  try {
    await request.put(`navigation/modules/company/${companyId}`, modules);
    return true;
  } catch (error) {
    console.error('Failed to update company module config:', error);
    return false;
  }
}

/**
 * Get all companies' module configs (Operator only).
 */
export async function getAllCompanyModuleConfigs(): Promise<CompanyModuleConfigView[]> {
  try {
    const response = await request.get<Result<CompanyModuleConfigView[]>>('navigation/modules/all');
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch all company module configs:', error);
    return [];
  }
}

/**
 * Get page configuration for current user's company.
 * Returns empty array on error (show all pages by default).
 */
export async function getPageConfig(): Promise<PageConfigView[]> {
  try {
    const response = await request.get<Result<PageConfigView[]>>('navigation/pages');
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch page config:', error);
    return []; // Return empty on error (show all pages by default)
  }
}

/**
 * Get page configuration for a specific company (Operator only).
 */
export async function getCompanyPageConfig(companyId: number): Promise<PageConfigView[]> {
  try {
    const response = await request.get<Result<PageConfigView[]>>(
      `navigation/pages/company/${companyId}`
    );
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch company page config:', error);
    return [];
  }
}

/**
 * Update page configuration for a company (Operator only).
 */
export async function updateCompanyPageConfig(
  companyId: number,
  pages: PageConfigInput[]
): Promise<boolean> {
  try {
    await request.put(`navigation/pages/company/${companyId}`, pages);
    return true;
  } catch (error) {
    console.error('Failed to update company page config:', error);
    return false;
  }
}
